import type { LanguageCode } from '@sparted/shared-library/business/types';
import { t } from 'i18next';
import React, { useEffect, useMemo, useState } from 'react';

import MDrawer from 'Components/modal/MDrawer/MDrawer';
import SFilterBox from 'Components/structural/SFilterBox/SFilterBox';
import UButton from 'Components/unit/UButton/UButton';
import UButtonWithLoading from 'Components/unit/UButton/UButtonWithLoading';
import UIconButton from 'Components/unit/UIconButton/UIconButton';
import { FilterState } from 'Libs/filter/types';
import { formatTranslationStatuses } from 'Pages/Content/ContentList/utils';
import { DrawerContent } from 'Store/entities/content/content-drawer/content-drawer.types';

import { DraggableContentCard } from '../DraggableContentCard/DraggableContentCard';
import styles from './ContentDrawer.style';
import { useContents } from './hooks/useContents';

export type ContentDrawerProps = {
  selectedLanguage: LanguageCode;
  visible: boolean;
  onClose: () => void;
  onNewContent: () => void;
  onItemsSelect: (items: DrawerContent[]) => void;
  alreadyUsedItemsId?: Set<number>;
};

const NB_CONTENTS_PER_PAGE = 21;

export const ContentDrawer: React.FC<ContentDrawerProps> = ({
  visible,
  selectedLanguage,
  onClose,
  onNewContent,
  onItemsSelect,
  alreadyUsedItemsId,
}) => {
  const [selectedItems, setSelectedItems] = useState<DrawerContent[]>([]);
  const [filters, setFilters] = useState({
    languageCode: selectedLanguage,
    names: [] as string[],
    thematicIds: [] as number[],
  });

  const hasFilters = useMemo(() => !!filters.thematicIds.length || !!filters.names.length, [filters]);

  const { contents, loading, total, setOffset, fetchContents } = useContents({
    filters,
    limit: NB_CONTENTS_PER_PAGE,
  });

  useEffect(() => {
    fetchContents();
  }, [fetchContents]);

  const description = t('activities:scheduling.drawer.description');

  if (!contents) return null;

  const onFilterChange = (newFilters: FilterState[]) => {
    setOffset(0);

    newFilters.forEach((filter) => {
      if (filter.category === 'thematic') {
        setFilters((oldFilter) => ({ ...oldFilter, thematicIds: filter.ids }));
      }
      if (filter.category === 'text') {
        setFilters((oldFilters) => ({ ...oldFilters, names: filter.values }));
      }
    });
  };

  const handleClear = () => {
    setSelectedItems([]);
  };

  const handleSelectItems = () => {
    onItemsSelect(selectedItems);
    setSelectedItems([]);
  };

  const selectItem = (item: DrawerContent) => {
    setSelectedItems([...selectedItems, item]);
  };
  const fetchNextPage = async () => {
    setOffset((prevOffset) => prevOffset + NB_CONTENTS_PER_PAGE);
  };

  const unselectItem = (item: DrawerContent) => {
    setSelectedItems(selectedItems.filter((selectedItem) => selectedItem.id !== item.id));
  };

  const handleClose = () => {
    setSelectedItems([]);

    onClose();
  };

  const footer = selectedItems.length ? (
    <div style={styles.selection}>
      <div style={styles.selectionText}>
        <UIconButton icon="close" size="M" onClick={handleClear} ghost type="standard-light" />
        <div style={styles.selectedItems}>
          {t('activities:scheduling.drawer.selected_items', { count: selectedItems.length })}
        </div>
      </div>
      <UButton
        text={t('activities:scheduling.drawer.select')}
        onClick={handleSelectItems}
        style={styles.selectButton}
      />
    </div>
  ) : null;

  return (
    <MDrawer visible={visible} onClose={handleClose} title={t('activities:scheduling.drawer.title')} footer={footer}>
      <div style={styles.container}>
        <SFilterBox
          key="filterbox-content-scheduling"
          placeholder={t('activities:scheduling.drawer.search_placeholder')}
          description={description}
          filters={[
            { category: 'thematic', ids: filters.thematicIds },
            { category: 'text', values: filters.names },
          ]}
          onChange={onFilterChange}
          count={total}
          type="compact"
          style={styles.filter}
          containerStyle={styles.filterContainer}
          showLanguageInSeg={false}
        />
        {contents.length === 0 && !loading ? (
          <div style={styles.empty}>
            <div style={styles.emptyTitle}>
              {hasFilters
                ? t('activities:scheduling.drawer.search_empty')
                : t('activities:scheduling.drawer.empty_library')}
            </div>
            <div style={styles.emptySubTitle}>
              {hasFilters
                ? t('activities:scheduling.drawer.search_empty_subtitle')
                : t('activities:scheduling.drawer.empty_library_subtitle')}
            </div>
            {hasFilters ? null : (
              <UButton
                text={t('activities:scheduling.drawer.create_content')}
                type="accentuated"
                onClick={onNewContent}
                style={styles.newContentButton}
              />
            )}
          </div>
        ) : null}

        <div style={styles.contentContainer}>
          {(contents ?? []).map((content) => {
            const selected = !!selectedItems.find((item) => item.id === content.id);

            const alreadyUsed = alreadyUsedItemsId?.has(content.id);

            return (
              <DraggableContentCard
                draggable
                dragId={content.id}
                key={content.id}
                content={content as any}
                contentCardProps={{
                  id: `#${content.customId || content.id}`,
                  previewable: false,
                  onSelect: (newSelected) => (newSelected ? selectItem(content) : unselectItem(content)),
                  selected,
                  alreadyUsed,
                  displayOption: false,
                  title: content.title?.[selectedLanguage] || '',
                  type: content.gameType,
                  translationStatuses: formatTranslationStatuses(content.statuses, content.id),
                  imageUrl: content?.coverUrl || '',
                  selectable: true,
                  canBeDragged: true,
                }}
              />
            );
          })}
        </div>

        {contents.length && total !== contents.length ? (
          <div style={styles.loadMore}>
            <UButtonWithLoading
              loading={loading}
              text={t('activities:scheduling.drawer.load_more')}
              onClick={!loading ? fetchNextPage : async () => {}}
              onRequestEnd={() => {}}
              ghost
            />
          </div>
        ) : null}
      </div>
    </MDrawer>
  );
};
